/**
 * ComparisonSection Component
 *
 * Collapsible section that groups comparison field rows with change count badge.
 */

import { Collapse, Badge, Typography, Space } from 'antd'
import { CaretRightOutlined } from '@ant-design/icons'
import type { ComparisonSectionProps } from './types'
import { ComparisonRow } from './ComparisonRow'

const { Text } = Typography

/**
 * Section grouping comparison field rows.
 *
 * @example
 * ```tsx
 * <ComparisonSection
 *   section={pricingSection}
 *   fields={metadata.fields}
 *   keyFields={new Set(['price', 'cost'])}
 *   expanded={true}
 *   onExpandChange={(exp) => setExpanded(exp)}
 *   mode="directed"
 * />
 * ```
 */
export function ComparisonSection({
  section,
  fields,
  keyFields,
  expanded,
  onExpandChange,
  mode,
  hasComparison,
  // showDifferencesOnly not used directly - passed for context
  temporalContext,
  leftColumnColor,
  rightColumnColor,
}: ComparisonSectionProps): JSX.Element {
  const { key, title, fields: sectionFields, changedCount } = section
  const totalCount = sectionFields.length

  // Collapsible sections with change count badges
  const handleCollapseChange = (keys: string | string[]) => {
    const isExpanded = Array.isArray(keys) ? keys.includes(key) : keys === key
    onExpandChange(isExpanded)
  }

  const headerContent = (
    <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
      <Text strong style={{ fontSize: 14 }}>
        {title}
      </Text>
      {changedCount > 0 && (
        <Badge
          count={changedCount}
          style={{
            backgroundColor: 'var(--rp-gray-500)',
            fontSize: 11,
          }}
          title={`${changedCount} field${changedCount !== 1 ? 's' : ''} changed`}
        />
      )}
    </Space>
  )

  return (
    <Collapse
      activeKey={expanded ? [key] : []}
      onChange={handleCollapseChange}
      expandIcon={({ isActive }) => (
        <CaretRightOutlined
          rotate={isActive ? 90 : 0}
          style={{ fontSize: 12 }}
          aria-hidden="true"
        />
      )}
      className="comparison-section"
      items={[
        {
          key: key,
          label: headerContent,
          children: (
            <div
              role="region"
              aria-label={`${title} field comparisons`}
              className="comparison-section-content"
            >
              {sectionFields.length === 0 ? (
                <div className="comparison-section-empty">
                  <Text type="secondary">No fields in this section</Text>
                </div>
              ) : (
                <div role="list" aria-label={hasComparison ? `${changedCount} of ${totalCount} fields changed` : `${totalCount} fields`}>
                  {sectionFields.map(diff => (
                    <ComparisonRow
                      key={diff.fieldKey}
                      difference={diff}
                      metadata={fields[diff.fieldKey]}
                      isKeyField={keyFields.has(diff.fieldKey)}
                      mode={mode}
                      hasComparison={hasComparison}
                      temporalContext={temporalContext}
                      leftColumnColor={leftColumnColor}
                      rightColumnColor={rightColumnColor}
                    />
                  ))}
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  )
}

export default ComparisonSection
